import React from "react";
import { ContentCopy, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionSummary,
  Typography,
  ButtonGroup,
  Button,
  AccordionDetails,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  Divider,
  colors,
  IconButton,
  Box,
} from "@mui/material";

import { Draggable } from "react-beautiful-dnd";

import { FormValuesType } from "pages";

import { theme } from "constants/theme";

export const FieldComponent: React.FC<{
  index: number;
  item: FormValuesType;
  edit?: boolean;
  isEditing?: boolean;
  editHandler?: () => void;
  deleteHandler?: () => void;
  resetFormWithValues: () => void;
}> = ({
  index,
  item,
  edit = false,
  editHandler,
  deleteHandler,
  resetFormWithValues,
  isEditing = false,
}) => {
  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={isEditing}>
      {(provided, snapshot) => (
        <Box
          style={{
            overflow: "hidden",
          }}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Accordion
            style={{
              flex: 1,
              // alignSelf: "center",
              backgroundColor:
                snapshot.isDragging || isEditing
                  ? colors.blueGrey["300"]
                  : "white",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls={`${item.fieldName}+${index}`}
              id={`${item.fieldName}+${index}`}
              sx={{
                flexWrap: "wrap",
              }}
            >
              <Typography
                style={{
                  maxWidth: "250px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {item.fieldName}
              </Typography>

              <ButtonGroup
                variant="contained"
                aria-label="edit field group"
                style={{
                  marginLeft: "auto",
                  alignSelf: "center",
                }}
              >
                {edit ? (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      editHandler?.();
                    }}
                  >
                    Edit
                  </Button>
                ) : null}
                <Button
                  onClick={() => {
                    deleteHandler?.();
                  }}
                >
                  Delete
                </Button>
              </ButtonGroup>

              <IconButton
                aria-label="Copy to add field"
                onClick={(e) => {
                  e.stopPropagation();
                  resetFormWithValues();
                }}
              >
                <ContentCopy />
              </IconButton>
            </AccordionSummary>

            <AccordionDetails style={{ wordBreak: "break-all" }}>
              <List
                aria-labelledby="subheader"
                subheader={
                  <ListSubheader component={"div"} id="subheader">
                    Field Data
                  </ListSubheader>
                }
                sx={{
                  border: theme.borderPrimary,
                  borderRadius: theme.borderRadius,
                  p: theme.commonPadding,
                }}
              >
                <ListItemButton disabled>
                  <ListItemText primary={"Field label: " + item.fieldName} />
                </ListItemButton>
                <Divider />

                <ListItemButton disabled>
                  <ListItemText primary={"Unique name: " + item.uniqueName} />
                </ListItemButton>
                <Divider />

                {item.fieldPlaceholder ? (
                  <>
                    <ListItemButton disabled>
                      <ListItemText
                        primary={"Field placeholder: " + item.fieldPlaceholder}
                      />
                    </ListItemButton>
                    <Divider />
                  </>
                ) : null}

                {item.globalRef ? (
                  <>
                    <ListItemButton disabled>
                      <ListItemText
                        primary={"Unique reference name: " + item.globalRef}
                      />
                    </ListItemButton>
                    <Divider />
                  </>
                ) : null}

                <ListItemButton disabled>
                  <ListItemText primary={"Field type: " + item.fieldType} />
                </ListItemButton>

                <Divider />

                {item.fieldType === "files" ? (
                  <>
                    <ListItemButton disabled>
                      <ListItemText primary={"Files type: " + item.fileType} />
                    </ListItemButton>
                    <Divider />
                  </>
                ) : null}

                {/* Checkbox info */}
                {item.fieldType === "checkbox" ? (
                  <>
                    {item.checkboxOptions.length ? (
                      <>
                        <ListItemButton disabled>
                          <ListItemText primary={"Checkbox options: "} />
                        </ListItemButton>
                        <List
                          component={"ol"}
                          sx={{ pl: 4, listStyle: "disc" }}
                        >
                          {item.checkboxOptions.map((op, i) => {
                            return (
                              <ListItemButton key={op.option + i} disabled>
                                <ListItemText
                                  primary={op.option}
                                  key={op.option + i}
                                  style={{ display: "list-item" }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                        <Divider />
                      </>
                    ) : null}

                    {item.acceptableValuesKey ? (
                      <>
                        <ListItemButton disabled>
                          <ListItemText
                            primary={
                              "Dropdown key: " + item.acceptableValuesKey
                            }
                            sx={{ pl: 2 }}
                          />
                        </ListItemButton>
                        <Divider />
                      </>
                    ) : null}
                    <ListItemButton disabled>
                      <ListItemText
                        primary={
                          "Allow multi select: " + item.multiOptionsSelect
                        }
                        sx={{ pl: 2 }}
                      />
                    </ListItemButton>
                    <Divider />
                  </>
                ) : null}

                {/* Checkbox info */}
                {item.fieldType === "array" ? (
                  <>
                    <ListItemButton disabled>
                      <ListItemText primary={"Nested questions: "} />
                    </ListItemButton>
                    <List component={"ol"} sx={{ pl: 4, listStyle: "disc" }}>
                      {item.nestedQuestions.map((op, i) => {
                        return (
                          <React.Fragment key={op.fieldName + i}>
                            <ListItemButton disabled>
                              <ListItemText
                                primary={"Question: " + op.fieldName}
                                style={{ display: "list-item" }}
                              />
                            </ListItemButton>

                            <ListItemButton disabled>
                              <ListItemText
                                primary={"Unique name: " + op.uniqueName}
                              />
                            </ListItemButton>

                            {op.fieldPlaceholder ? (
                              <ListItemButton disabled>
                                <ListItemText
                                  primary={
                                    "Field placeholder: " + op.fieldPlaceholder
                                  }
                                />
                              </ListItemButton>
                            ) : null}

                            <ListItemButton disabled>
                              <ListItemText
                                primary={"Question type: " + op.fieldType}
                              />
                            </ListItemButton>

                            {op.fieldType === "files" ? (
                              <ListItemButton disabled>
                                <ListItemText
                                  primary={"Files type: " + op.fileType}
                                />
                              </ListItemButton>
                            ) : null}

                            {/* Checkbox info */}
                            {op.fieldType === "checkbox" ? (
                              <>
                                {op.checkboxOptions.length ? (
                                  <>
                                    <ListItemButton disabled>
                                      <ListItemText
                                        primary={"Checkbox options: "}
                                      />
                                    </ListItemButton>
                                    <List
                                      component={"ol"}
                                      sx={{ pl: 4, listStyle: "disc" }}
                                    >
                                      {op.checkboxOptions.map((op, i) => {
                                        return (
                                          <ListItemButton
                                            key={op.option + i}
                                            disabled
                                          >
                                            <ListItemText
                                              primary={op.option}
                                              key={op.option + i}
                                              style={{ display: "list-item" }}
                                            />
                                          </ListItemButton>
                                        );
                                      })}
                                    </List>
                                  </>
                                ) : null}

                                {op.acceptableValuesKey ? (
                                  <ListItemButton disabled>
                                    <ListItemText
                                      primary={
                                        "Dropdown key: " +
                                        op.acceptableValuesKey
                                      }
                                      sx={{ pl: 2 }}
                                    />
                                  </ListItemButton>
                                ) : null}

                                <ListItemButton disabled>
                                  <ListItemText
                                    primary={
                                      "Allow multi select: " +
                                      op.multiOptionsSelect
                                    }
                                    sx={{ pl: 2 }}
                                  />
                                </ListItemButton>

                                <ListItemButton disabled>
                                  <ListItemText
                                    primary={
                                      "Required: " + op.validate_required
                                    }
                                    style={{ display: "list-item" }}
                                  />
                                </ListItemButton>
                              </>
                            ) : null}
                          </React.Fragment>
                        );
                      })}
                    </List>
                    <Divider />
                  </>
                ) : null}

                {/* Validation info */}
                <ListItemButton disabled>
                  <ListItemText primary={"Validation info: "} />
                </ListItemButton>
                <List component={"ol"} sx={{ pl: 4, listStyle: "disc" }}>
                  <ListItemButton disabled>
                    <ListItemText
                      primary={"Required: " + item.validate_required}
                      style={{ display: "list-item" }}
                    />
                  </ListItemButton>
                </List>
              </List>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Draggable>
  );
};
